import { useEffect, useRef, useState } from 'react';
import * as PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import { scroller } from 'react-scroll';
import { useMedia } from 'use-media';
import { get_, rules, dataTransformers, htmlToPlainText } from 'sf-modules';
import useStateEngine from '@yema/commerce/useStateEngine';
import useTracker from '@yema/commerce/hooks/tracking/useTracker';
import { PATH_PRODUCT_PAGE } from 'constants/paths';
import Blobs from '@/yema-designkit/webkit/atoms/Blobs';
import Hr from '@/yema-designkit/webkit/atoms/Hr';
import UspImageText from '@/yema-designkit/webkit/atoms/UspImageText';
import ProductImageSlider from '@/yema-designkit/webkit/molecules/ProductImageSlider';
import ProductDetails from '@/yema-designkit/webkit/organisms/ProductDetails';
import RichContent from '@/yema-designkit/schemas/RichContent';
import Link from '@/yema-designkit/webkit/atoms/Link';
import Rating from '@/yema-designkit/webkit/atoms/Rating';
import Usp from '@/yema-designkit/webkit/molecules/Usp';
import YDSProductPageBreadcrumb from './YDSProductPageBreadcrumb';
import ProductPriceForQuantity from '@/yema-designkit/webkit/atoms/Product/ProductPriceForQuantity';
import { getProductsAvailability } from 'functions/fnRequestAPIs/fnProductsAPI';
import { fetchProductsPromotions } from 'functions/fnRequestAPIs/fnProductsPromotion';

const AddToCart = dynamic(() => import('@yema/commerce/components/Product/AddToCart'), {
    ssr: false,
});

const AddToCartSticky = dynamic(() => import('@yema/commerce/components/Product/AddToCartSticky'), {
    ssr: false,
});

const ProductReviews = dynamic(() => import('./ProductReviews'), { ssr: false });
const YDSProductSlider = dynamic(() => import('./YDSProductSlider'), { ssr: false });
const YDSRelatedProductsSlider = dynamic(() => import('./YDSRelatedProductsSlider'), {
    ssr: false,
});
const FullModal = dynamic(() => import('@/yema-designkit/webkit/organisms/FullModal/FullModal'), {
    ssr: false,
});

const ProductPage = ({ uspPromo, uspPromise, product, productBreadcrumb }) => {
    const INITIAL_MODAL_CONTENT = {
        title: '',
        content: '',
        isOpen: false,
    };

    const [{ catalog }] = useStateEngine();

    const isMobile = useMedia({ maxWidth: 640 });
    const isMobileXS = useMedia({ maxWidth: 320 });

    const { trackEvent } = useTracker();

    const [pageIsReady, setPageIsReady] = useState(false);
    const [fetchedCatalog, setFetchedCatalog] = useState(null);
    const [catalogPrice, setCatalogPrice] = useState(product?.catalogPrice || null);
    const [availability, setAvailability] = useState(null);
    const [firstApplicableRule, setFirstApplicableRule] = useState(null);
    const [bundleProducts, setBundleProducts] = useState([]);
    const [showSticky, setShowSticky] = useState(false);
    const [modalContent, setModalContent] = useState(INITIAL_MODAL_CONTENT);

    const productDescriptionPlainText = htmlToPlainText(product.description);

    const refetchAvailabilitiesAndPromotions = async ({
        shouldRefetchAvailabilities = false,
        shouldRefetchPromotions = false,
    }) => {
        if (!catalog || !catalog.zipCode) return;

        let gtinsToFetch = [];
        let bundlesIdToFetch = [];

        if (product.isBundle) bundlesIdToFetch.push(product.sku);
        else gtinsToFetch.push(product.gtin);

        const productIds = get_(product, ['rawId']);
        const plmIds = get_(product, ['plmId']);

        const bundlesSkus = get_(product, ['skus'], []);
        gtinsToFetch = [...gtinsToFetch, ...bundlesSkus];

        const fetchedPromotionsPromise = shouldRefetchPromotions
            ? fetchProductsPromotions({
                  gtinsToFetch,
                  bundlesIdToFetch,
                  plmIds: plmIds ? [plmIds] : null,
                  productIds: productIds ? [productIds] : null,
                  storeId: catalog?.store?.id,
              })
            : null;

        const fetchedAvailabilitiesPromise = shouldRefetchAvailabilities
            ? getProductsAvailability({
                  gtins: [...gtinsToFetch, ...bundlesIdToFetch],
                  plmIds: plmIds ? [plmIds] : null,
                  productIds: productIds ? [productIds] : null,
                  zipCode: catalog ? catalog.zipCode : null,
                  storeId: catalog?.store?.id,
              })
            : null;

        Promise.all([fetchedPromotionsPromise, fetchedAvailabilitiesPromise]).then((res) => {
            const fetchedPromotions = get_(res, [0]);
            const fetchedAvailabilities = get_(res, [1]);

            // Set main product availability
            if (shouldRefetchAvailabilities) {
                const mainProductAvailability = fetchedAvailabilities?.availabilities?.find(
                    (x) => x.gtin === product.gtin
                );
                const parsedAvailability = dataTransformers.parseAvailability(
                    catalog?.zipCode,
                    product,
                    mainProductAvailability
                );

                setCatalogPrice(fetchedAvailabilities?.prices?.find((x) => x.sku === product.gtin));
                setAvailability(parsedAvailability);
                setFetchedCatalog(catalog.zipCode);
            }

            // Set main product promotions
            if (shouldRefetchPromotions) {
                const mainProductPromotion = product.isBundle
                    ? fetchedPromotions?.bundlesPromotions.find((x) => x.sku === product.gtin)
                    : fetchedPromotions?.productsPromotions.find((x) => x.gtin === product.gtin);
                const firstApplicableRule = rules.getFirstApplicableRule(
                    get_(mainProductPromotion, ['applicableRules'], []),
                    product.gtin
                );
                setFirstApplicableRule({
                    title: get_(firstApplicableRule, ['title']),
                    icon: get_(firstApplicableRule, ['campaignIcon']),
                });
            }

            // Set bundles products information
            if (product.isBundle && bundlesSkus.length) {
                const products =
                    bundleProducts && bundleProducts.length ? bundleProducts : product.products;
                setBundleProducts(
                    products.map((p) => {
                        const availability = fetchedAvailabilitiesPromise
                            ? {
                                  ...p.availability,
                                  ...dataTransformers.parseAvailability(
                                      catalog?.zipCode,
                                      p,
                                      fetchedAvailabilities?.availabilities?.find(
                                          (x) => x.gtin === p.gtin
                                      )
                                  ),
                              }
                            : p.availability;

                        const firstApplicableRule = !shouldRefetchPromotions
                            ? p.firstApplicableRule
                            : get_(
                                  rules.getFirstApplicableRule(
                                      get_(
                                          fetchedPromotions.productsPromotions.find(
                                              (x) => x.gtin === p.gtin
                                          ),
                                          ['applicableRules'],
                                          []
                                      ),
                                      p.gtin
                                  ),
                                  ['title']
                              );

                        return {
                            ...p,
                            availability: availability,
                            firstApplicableRule: firstApplicableRule,
                            catalogPrice: fetchedAvailabilities?.prices?.find(
                                (x) => x.sku === p.gtin
                            ),
                        };
                    })
                );
            }

            // All pricing, availability & promotions infos is loaded
            setPageIsReady(true);
        });
    };

    useEffect(() => {
        if (pageIsReady) {
            trackEvent({
                type: 'Product Viewed',
                data: {
                    rawProduct: {
                        ...product,
                        catalogPrice: catalogPrice,
                    },
                },
            });
        }
    }, [pageIsReady]);

    useEffect(() => {
        if (fetchedCatalog) {
            setPageIsReady(false);
            refetchAvailabilitiesAndPromotions({
                shouldRefetchAvailabilities: true,
                shouldRefetchPromotions: true,
            });
        }

        // This useEffect should be triggered only on product change (i.e. client-side navigation)
    }, [product]);

    useEffect(() => {
        if (catalog && catalog.createdAt && catalog.createdAt !== '') {
            refetchAvailabilitiesAndPromotions({
                shouldRefetchAvailabilities: true,
                shouldRefetchPromotions: true, // Promotions depends on store change
            });
        }

        // This useEffect should be triggered only on zipCode or store change
    }, [catalog?.zipCode, catalog?.store?.id]);

    const stickyRef = useRef(null);

    useEffect(() => {
        if (isMobile) {
            function handleScroll() {
                if (stickyRef?.current !== null && stickyRef?.current?.offsetLeft !== null)
                    setShowSticky(stickyRef.current.getBoundingClientRect().top < 60);
            }

            window.addEventListener('scroll', handleScroll);
            return () => {
                window.removeEventListener('scroll', handleScroll);
            };
        }
    });

    return (
        <>
            <RichContent product={product} catalogPrice={catalogPrice} />

            <YDSProductPageBreadcrumb productBreadcrumb={productBreadcrumb} />

            {modalContent && modalContent.isOpen && (
                <FullModal
                    title={modalContent.title}
                    isOpen={modalContent.isOpen}
                    overlayClassName='bg-ui-white sm:bg-overlay-primary-100'
                    headerBackgroundColor='bg-brand-primary-300 sm:bg-transparent'
                    onClose={() => setModalContent(INITIAL_MODAL_CONTENT)}
                >
                    {modalContent.content}
                </FullModal>
            )}

            <div className='relative'>
                <div className='container'>
                    <div className='mb-4 flex flex-col lg:flex-row lg:gap-x-6'>
                        <div className='w-full lg:w-1/2'>
                            <ProductImageSlider
                                productName={product.name}
                                images={product.images}
                            />
                        </div>
                        <div className='w-full lg:w-1/2'>
                            <div className='mt-4 flex flex-row items-center justify-between lg:mt-0'>
                                {product.brand && (
                                    <Link
                                        className='mt-1 font-body text-ui-gray-500 hover:text-ui-gray-700'
                                        href={`/b/${product.brand}`}
                                        underline
                                    >
                                        {product.brand}
                                    </Link>
                                )}
                                {!product.isBundle && (
                                    <div
                                        className='ml-8 flex items-center'
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => {
                                            scroller.scrollTo('resena', {
                                                isDynamic: true,
                                                duration: 600,
                                                delay: 100,
                                                smooth: true,
                                                offset: -120,
                                            });
                                        }}
                                    >
                                        <Rating
                                            size={isMobileXS ? 'sm' : 'md'}
                                            review={get_(product, ['reviews', 'count'])}
                                            rating={get_(product, ['reviews', 'average'], 0)}
                                        />
                                    </div>
                                )}
                            </div>
                            <Blobs blobs={product.blobs} name={product.name} />
                            <ProductPriceForQuantity
                                pricesWrapperClassName='flex-col'
                                discountsWrapperClassName='mb-0.5'
                                context='product-page'
                                product={{
                                    ...product,
                                    catalogPrice: catalogPrice,
                                }}
                            />
                            <Hr />
                            {productDescriptionPlainText.length > 150 ? (
                                <p>
                                    {productDescriptionPlainText.substring(0, 150)}
                                    ...{' '}
                                    <Link
                                        onClick={() => {
                                            setModalContent({
                                                title: 'Acerca de',
                                                isOpen: true,
                                                content: (
                                                    <div
                                                        dangerouslySetInnerHTML={{
                                                            __html: product.description,
                                                        }}
                                                    />
                                                ),
                                            });
                                        }}
                                        className='cursor-pointer underline'
                                    >
                                        Leer más
                                    </Link>
                                </p>
                            ) : (
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: product.description,
                                    }}
                                />
                            )}
                            <Hr className='mt-2 mb-4' />
                            {product.certifications && (
                                <div className='m-0 flex w-full flex-row flex-wrap'>
                                    {product.certifications.map((certificate, index) => (
                                        <UspImageText
                                            key={'certificate-' + index}
                                            image={certificate}
                                            text={certificate.name}
                                            className='mb-5 inline-block w-1/4 last:mr-0 sm:mr-4 sm:w-20'
                                            textSize='text-[.65rem] sm:text-caption'
                                        />
                                    ))}
                                </div>
                            )}
                            <div className='mb-6' ref={stickyRef}>
                                <AddToCart
                                    context='productPage'
                                    product={{
                                        ...product,
                                        availability: availability,
                                        firstApplicableRule: firstApplicableRule,
                                    }}
                                />
                            </div>

                            {uspPromo && (
                                <Usp
                                    image={uspPromo.icon}
                                    content={uspPromo.text}
                                    altText='Envío'
                                    context='promo'
                                />
                            )}
                        </div>
                    </div>

                    {Boolean(product.isBundle && bundleProducts.length) && (
                        <div>
                            <h3>Cómpralos por separado</h3>
                            <YDSProductSlider key={'Slider0'} products={bundleProducts} />
                        </div>
                    )}
                </div>

                <ProductDetails
                    isMobile={isMobile}
                    product={product}
                    modalContent={modalContent}
                    setModalContent={setModalContent}
                    PATH_PRODUCT_PAGE={PATH_PRODUCT_PAGE}
                />

                {!product.isBundle && <YDSRelatedProductsSlider product={product} />}

                {!product.isBundle && <ProductReviews product={product} uspPromise={uspPromise} />}
            </div>

            {/* STICKY STEPPER MOBILE */}
            {isMobile && (
                <AddToCartSticky
                    show={showSticky}
                    context='productPage'
                    catalog={catalog}
                    product={{
                        ...product,
                        availability: availability,
                        catalogPrice: catalogPrice,
                        firstApplicableRule: firstApplicableRule,
                    }}
                />
            )}
        </>
    );
};

ProductPage.defaultProps = {};

ProductPage.propTypes = {
    uspPromo: PropTypes.object,
    uspPromise: PropTypes.object,
    product: PropTypes.object.isRequired,
    productBreadcrumb: PropTypes.array,
};

export default ProductPage;
